import { Injectable, WritableSignal, computed, inject, signal } from '@angular/core';
import { IModelsToContentItemsConverter } from '@frontend/core';
import { AuthService, UserExtraLite } from '../auth';
import { ContentItem, MediaService } from '../content';
import { Language, LanguageService, LanguageSkill } from '../language';
import { CultureService } from '../cultures';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  filter,
  finalize,
  map,
  of,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ErrorService } from '../error';
import { LoadingObject, LoadingService } from '../loaders';

export type UpdateUserProfileRequestBody = {
  job_title?: string | null;
  summary?: string | null;
  bio?: string | null;
};

export type ProfileUserPatchResource = {
  id: number;
  user_id: number;
  job_title: string | null;
  summary: string | null;
  bio: string | null;
  created_at: string;
  updated_at: string;
};

type GetUserProfileSuccessResponse = {
  data: ProfileUserPatchResource;
};

type UpdateUserProfileSuccessResponse = {
  data: ProfileUserPatchResource;
};

@Injectable({
  providedIn: 'root'
})
export class ProfileUserService implements IModelsToContentItemsConverter {

  // Locale-keyed cache: the user's languages are personal data whose display
  // names are translated, so we hold one list per UI language in parallel and
  // never refetch a language we already have. `clearData()` clears every locale
  // on logout; switching UI language never discards other locales.
  private _myLanguagesByLocale : WritableSignal<Record<string, Language[]>> = signal({});
  /** The user's languages for the currently-active UI language (undefined until fetched). */
  readonly myLanguages = computed<Language[] | undefined>(
    () => this._myLanguagesByLocale()[this.languageService.activeLanguage().languageKey],
  );
  private _userProfile = new BehaviorSubject<ProfileUserPatchResource | null | undefined>(undefined);
  private _loadingObject = signal<LoadingObject>({});
  private loadingService = inject(LoadingService);
  activeLanguageSubscription: Subscription;
  myLanguagesSubscription: Subscription | null = null;


  constructor(
    private mediaService : MediaService,
    private languageService : LanguageService,
    private cultureService : CultureService,
    private http: HttpClient,
    private errorService: ErrorService,
    private authService: AuthService,
  ) {

    // On a genuine locale change, keep the active locale populated for anyone
    // currently viewing their languages — but only if the feature is already in
    // use (at least one locale cached) and the new locale isn't cached yet. Other
    // locales are left intact so switching back is instant and free.
    this.activeLanguageSubscription =
    this.languageService.activeLanguageChanges$.subscribe(() => {
      const featureInUse = Object.keys(this._myLanguagesByLocale()).length > 0;
      if (this.authService.user.getValue() && featureInUse && !this.myLanguages()) {
        this.myLanguagesSubscription = this.getMyLanguages(false).subscribe();
      }
    });

  }

  clearData() {
    this._myLanguagesByLocale.set({});
    this._userProfile.next(undefined);
  }

  private get activeLanguageKey(): string {
    return this.languageService.activeLanguage().languageKey;
  }

  /** Replace the active locale's list, leaving other locales cached. */
  private setMyLanguagesForActiveLocale(languages: Language[]): void {
    this._myLanguagesByLocale.update((byLocale) => ({ ...byLocale, [this.activeLanguageKey]: languages }));
  }

  /**
   * Apply a fully locale-independent change (e.g. deleting a language by id) to
   * every cached locale, preserving each locale's translated names/descriptions.
   */
  private applyToAllLocales(mutate: (languages: Language[]) => Language[]): void {
    this._myLanguagesByLocale.update((byLocale) => {
      const next: Record<string, Language[]> = {};
      for (const key of Object.keys(byLocale)) {
        next[key] = mutate(byLocale[key]);
      }
      return next;
    });
  }

  get loadingObject(): { [key: string]: boolean } {
    return this._loadingObject();
  }

  public get loadingObjectSignal(): WritableSignal<LoadingObject> {
    return this._loadingObject;
  }

  isAnythingLoading(): boolean {
    return this.loadingService.isAnythingLoading(this._loadingObject());
  }

  setLoadingStatus(key: string, status: boolean): void {
    this._loadingObject.set(this.loadingService.setLoadingStatus(key, status, this._loadingObject()));
  }

  get userProfile$(): Observable<ProfileUserPatchResource | null | undefined> {
    return this._userProfile.asObservable();
  }

  get userProfileState(): ProfileUserPatchResource | null | undefined {
    return this._userProfile.getValue();
  }

  getUserProfile(freshFromServer = false): Observable<ProfileUserPatchResource | null> {
    if (this.loadingObjectSignal()['userProfile']) {
      return this.userProfile$.pipe(
        filter((profile) => profile !== undefined),
        take(1),
        map((profile) => profile ?? null),
      );
    }

    if (!freshFromServer && this.userProfileState !== undefined) {
      return of(this.userProfileState ?? null);
    }

    this.setLoadingStatus('userProfile', true);

    return this.http.get<GetUserProfileSuccessResponse>('api/v1/user-profiles/my-profile').pipe(
      map((response) => {
        const profile = response?.data ?? null;
        this._userProfile.next(profile);
        return profile;
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        if (errorResponse.status === 404) {
          this._userProfile.next(null);
          return of(null);
        }
        if (errorResponse.status === 401) {
          return throwError({
            message: errorResponse.error?.message ?? 'Unauthenticated.',
            status: 401,
          });
        }
        return this.handleError(errorResponse);
      }),
      finalize(() => {
        this.setLoadingStatus('userProfile', false);
      }),
    );
  }

  updateUserProfile(payload: UpdateUserProfileRequestBody): Observable<ProfileUserPatchResource> {
    this.setLoadingStatus('userProfile', true);

    return this.http.patch<UpdateUserProfileSuccessResponse>('api/v1/user-profile', payload).pipe(
      map((response) => {
        const profile = response.data;
        this._userProfile.next(profile);
        return profile;
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        if (errorResponse.status === 422 && errorResponse.error?.errors) {
          return throwError({
            message: errorResponse.error?.message ?? 'The given data was invalid.',
            errors: errorResponse.error.errors,
            status: 422,
          });
        }
        if (errorResponse.status === 404) {
          return throwError({
            message: errorResponse.error?.message ?? 'User profile not found.',
            status: 404,
          });
        }
        if (errorResponse.status === 401) {
          return throwError({
            message: errorResponse.error?.message ?? 'Unauthenticated.',
            status: 401,
          });
        }
        return this.handleError(errorResponse);
      }),
      finalize(() => {
        this.setLoadingStatus('userProfile', false);
      }),
    );
  }

  getMyLanguages(freshFromServer:boolean): Observable<Language[]> {

    const cached = this.myLanguages();
    if (!freshFromServer && cached) {
      return of(cached);
    } else {
      return this.http.get<Language[]>('api/v1/user-profiles/my-languages').pipe(
        map((languages) => {
          this.setMyLanguagesForActiveLocale(languages);
          return languages;
        }),
        catchError((error)=>this.handleError(error)),
      );
    }
  }
  updateMyLanguage(languageSkill: LanguageSkill): Observable<Language[]> {
    return this.http.post('api/v1/user-profiles/my-languages/update', languageSkill).pipe(
      // The skill `level` is locale-independent (shared across locales), so an update
      // to it makes EVERY cached locale stale, not just the active one. The skill
      // `description` is locale-specific (the user writes it per language). So drop
      // all cached locales and reload the active one from the server — it returns the
      // new level plus that locale's own translated description. Other locales refetch
      // their own description (with the new level) on demand.
      switchMap(() => {
        this._myLanguagesByLocale.set({});
        return this.getMyLanguages(true);
      }),
      catchError((error)=>this.handleError(error)),
    );
  }

  storeNewMyLanguage(languageSkill: LanguageSkill): Observable<Language[]> {
    return this.http.post<Language[]>('api/v1/user-profiles/my-languages/store', languageSkill).pipe(
      map((languages) => {
        // A newly-added language's name is only known in the active locale, so
        // drop the other (now stale) locales — they refetch translated on demand.
        this._myLanguagesByLocale.set({ [this.activeLanguageKey]: languages });
        return languages;
      }),
      catchError((error)=>this.handleError(error)),
    );
  }

  deleteMyLanguage(languageId: number): Observable<Language[]> {
    return this.http.delete(`/api/v1/user-profiles/my-languages/${languageId}`).pipe(
      map(() => {
        // Deletion is locale-independent — remove the language from every locale.
        this.applyToAllLocales((languages) => languages.filter(language => language.id !== languageId));
        return this.myLanguages() ?? [];
      }),
      catchError((error)=>this.handleError(error)),
    );
  }

  convertUserToContentItem(user: UserExtraLite): ContentItem {
    const languagesAsTags = user.advancedLanguages ? this.languageService.convertLanguagesToTags(user.advancedLanguages,true,this.mediaService.cloudinary_base_url) : [];
    const culturesAsTags = user.cultures ? this.cultureService.convertCulturesToTags(user.cultures,true) : [];
    let contentItem = new ContentItem(
      user.id,
      user.name_full_honorific,
      null,
      null,
      null,
      user.bio,
      null,
      null,
      user.slug,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      [...languagesAsTags,...culturesAsTags],
      user,
      null
    );
    return contentItem;
  }
  
  makeContentItems(users: UserExtraLite[]): ContentItem[] {
    let content: ContentItem[] = [];
    if (users?.length) {
      content = users.map(user => this.convertUserToContentItem(user));
    }
    return content;
  }

  private handleError(errorResponse: HttpErrorResponse) {
    let errorMessage = 'error.something_went_wrong';
    if (!errorResponse.error || !errorResponse.error.message) {
      return throwError(errorMessage);
    } else {
      const message = errorResponse.error.message;
      const standardErrorMessageTranslationKey = this.errorService.getCommonErrorMessageTranslationKey(message);
      if(standardErrorMessageTranslationKey){
        errorMessage = standardErrorMessageTranslationKey;
      } else if(message.includes('Request latest data then try again.')){
        errorMessage = 'error.refresh';
      }
      if (errorResponse.error.meta){
        return throwError({message:errorMessage,meta:errorResponse.error.meta});
      }
      return throwError(errorMessage);
    }
  }


}